import React, { useEffect, useRef } from 'react';
import { FlatList, StyleSheet, View, TouchableOpacity, Text } from 'react-native';
import Tab from '../Tab/Tab';
import { useLinkTo } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/store';
import { setActiveTab } from 'src/slices/UiSlice';
import { DRIVER_BYPASS_URL, VEHICLE_BYPASS_URL } from 'src/routes/urlConstants';
import { trackTabsPressed } from 'src/helpers/gtm/gtmEvents';
import { driver, Driver } from 'driver.js';
import {
  detectRouteFromUrl,
  getTourConfig,
  TOUR_ENABLED,
  TOUR_TIMING,
} from 'src/features/tour/tourConfig';
import { logger } from 'src/helpers/logger';
import { applyTourTestIds } from 'src/features/tour/utils/tourTestIds';
import { themes } from 'src/theme/theme';
import { useDesktopLayout } from 'src/hooks/useDesktopLayout';

type TopNavigationProps = {
  tabs: {
    label: string;
    route: string;
    disabled: boolean;
  }[];
};

const TopNavigation: React.FC<TopNavigationProps> = ({ tabs }) => {
  const { isDesktop } = useDesktopLayout();
  const activeTab = useSelector((state: RootState) => state.UI.activeTab);
  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const vehicles = useSelector((state: RootState) => state.vehicles.vehicles);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const filledTabsIndex = useSelector((state: RootState) => state.UI.filledTabsIndex);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const dispatch = useDispatch();
  const linkTo = useLinkTo();
  const manualTourDriverRef = useRef<Driver | null>(null);

  // const [showTourHelp, setShowTourHelp] = useState(false);

  const handleTabPress = (index: number, route: string) => {
    // Track tab click events for analytics
    if (accountType === 'user') {
      trackTabsPressed(route, accountType);
    }

    if (index === 2 && drivers.length > 0) {
      linkTo(DRIVER_BYPASS_URL);
      dispatch(setActiveTab({ activeTab: index }));
      return;
    }
    if (index === 1 && vehicles.length > 0) {
      linkTo(VEHICLE_BYPASS_URL);
      dispatch(setActiveTab({ activeTab: index }));
      return;
    }
    dispatch(setActiveTab({ activeTab: index })); // Update active tab index
    linkTo(route); // Navigate to the pressed tab's page
  };

  const isTabDisabled = (index: number): boolean => {
    // Tabs 2–4 are enabled by default after "Get a Quote"
    if (index >= 1 && index <= 3) {
      return false;
    }
    // Tabs 5–6 retain existing gating logic
    return index > filledTabsIndex;
  };

  useEffect(() => {
    let navigated = false;
    if (
      activeTab === 2 &&
      drivers.length > 0 &&
      currentCompany &&
      currentCompany?.id !== 0 &&
      !navigated
    ) {
      linkTo(DRIVER_BYPASS_URL);
      navigated = true;
    }
    if (
      activeTab === 1 &&
      vehicles.length > 0 &&
      currentCompany &&
      currentCompany?.id !== 0 &&
      !navigated
    ) {
      linkTo(VEHICLE_BYPASS_URL);
      navigated = true;
    }
  }, [activeTab, drivers, vehicles]);

  // Cleanup manual tour driver on unmount
  useEffect(() => {
    return () => {
      if (manualTourDriverRef.current) {
        manualTourDriverRef.current.destroy();
        manualTourDriverRef.current = null;
      }
    };
  }, []);

  // Handle tour replay
  const handleTourHelp = () => {
    // Clean up any existing tour instance
    if (manualTourDriverRef.current) {
      manualTourDriverRef.current.destroy();
      manualTourDriverRef.current = null;
    }

    // Detect route from URL path
    let detectedRoute: string | undefined;
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      logger.debug('Detecting route from URL path:', path);
      detectedRoute = detectRouteFromUrl(path);
    }

    logger.debug('Tour help button clicked - Detected route:', detectedRoute);

    // Get tour configuration for the detected route
    const tourConfig = detectedRoute ? getTourConfig(detectedRoute) : undefined;

    if (!tourConfig) {
      logger.warn('No tour available for route:', detectedRoute);
      // TODO: Show user-friendly notification when Toast system is integrated
      // For now, just log the message
      return;
    }

    logger.debug('Starting manual tour replay:', tourConfig.title);

    // Create driver instance
    const driverObj = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      allowClose: true,
      popoverClass: 'tpi-tour-popover',
      steps: tourConfig.tourSteps(),
      onPopoverRender: applyTourTestIds,
      onDestroyed: () => {
        // Cleanup when tour ends
        if (manualTourDriverRef.current) {
          manualTourDriverRef.current.destroy();
          manualTourDriverRef.current = null;
        }
      },
    });

    manualTourDriverRef.current = driverObj;

    // Start tour after brief delay
    setTimeout(() => {
      try {
        driverObj.drive();
      } catch (error) {
        logger.error('Failed to start manual tour:', error);
        if (manualTourDriverRef.current) {
          manualTourDriverRef.current.destroy();
          manualTourDriverRef.current = null;
        }
      }
    }, TOUR_TIMING.MANUAL_START_DELAY_MS);
  };

  // Hide horizontal tabs on desktop — SideNavigation is used instead
  if (isDesktop) {
    return null;
  }

  return (
    <View style={styles.container}>
      <FlatList
        style={styles.list}
        data={tabs} // List of tab objects
        renderItem={({ item, index }) => (
          <View style={styles.item}>
            <Tab
              testID={`top-navigation-tab-${index}`}
              label={item.label}
              disabled={isTabDisabled(index)} // Disable only gated tabs
              active={activeTab === index} // Active state is true if the index matches
              onTabPress={() => handleTabPress(index, item.route)}
              key={index}
            />
          </View>
        )}
        keyExtractor={(item) => item.label}
        horizontal // Enables horizontal scrolling
        showsHorizontalScrollIndicator={false}
      />

      {/* Help Button for Manual Tour Trigger - Only for Producers */}
      {TOUR_ENABLED && currentCompany && currentCompany.id > 0 && accountType === 'producer' && (
        <TouchableOpacity
          testID="tour-help-button"
          style={styles.helpButton}
          onPress={handleTourHelp}>
          <Text testID="tour-help-button-text" style={styles.helpButtonText}>
            ?
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

export default TopNavigation;

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  list: { marginBottom: 4, paddingHorizontal: 16 },
  item: { paddingHorizontal: 8 },
  helpButton: {
    position: 'absolute',
    top: 8,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: themes.light.brand_blue[1],
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: themes.light.black[1],
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  helpButtonText: {
    color: themes.light.white[1],
    fontSize: 18,
    fontWeight: 'bold',
  },
});
